// Chat text size: small, medium (the default) or large, like Discord's chat font scaling. Saved to the account like
// the theme (/api/me/settings), and in localStorage so it applies before the first paint on this device. Applied as
// <html data-text-size>; the CSS scales message text, names, reply lines and the message and edit boxes from it.
import { useSyncExternalStore } from "react";
import { TEXT_SIZES, type TextSize } from "@bloom/shared";
import { api } from "./api.ts";

const KEY = "bloom:text-size";
const listeners = new Set<() => void>();

function stored(): TextSize {
  try {
    const v = localStorage.getItem(KEY) as TextSize | null;
    return v && TEXT_SIZES.includes(v) ? v : "medium";
  } catch {
    return "medium";
  }
}

let pref: TextSize = stored();

function apply() {
  document.documentElement.dataset.textSize = pref;
  for (const fn of listeners) fn();
}

/** Sets it on this device right away; `save` also stores it on the account for my other devices. */
export function setTextSize(next: TextSize, { save = true } = {}) {
  if (!TEXT_SIZES.includes(next)) return;
  pref = next;
  try {
    localStorage.setItem(KEY, next);
  } catch {
    // private mode: still applies for this page
  }
  apply();
  if (save) void api("/me/settings", { method: "PUT", body: { textSize: next } }).catch(() => {});
}

export const getTextSize = () => pref;

export function installTextSize() {
  apply();
  // Another tab changed it.
  addEventListener("storage", (e) => {
    if (e.key !== KEY) return;
    pref = stored();
    apply();
  });
}

const subscribe = (fn: () => void) => {
  listeners.add(fn);
  return () => listeners.delete(fn);
};

export const useTextSize = () => useSyncExternalStore(subscribe, getTextSize);
